import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect, Text as KonvaText, Transformer } from 'react-konva';
import type Konva from 'konva';
import { Button } from 'react-bootstrap';
import { Undo, Redo } from 'lucide-react';
import type { ImageEditorTool } from '../types';
import { sourcingClient } from '../../../../../../infrastructure/SourcingClient';
import { convertToS3Url } from '../utils/imageUtils';

interface TextElement {
  id: string;
  text: string;
  x: number;
  y: number;
  fontSize: number;
  fontFamily: string;
  fill: string;
}

interface HistoryState {
  imageUrl: string;
  texts: TextElement[];
}

interface ImageKonvaEditorProps {
  readonly imageUrl: string;
  readonly preloadedImage?: HTMLImageElement | undefined;
  readonly tool: ImageEditorTool;
  readonly textSettings?: { color: string; fontSize: number } | undefined;
  readonly renderActions?: () => React.ReactNode;
  readonly getCurrentEditedImage?: React.MutableRefObject<(() => string) | undefined>;
}

export const ImageKonvaEditor = ({
  imageUrl,
  preloadedImage,
  tool,
  textSettings,
  renderActions,
  getCurrentEditedImage,
}: ImageKonvaEditorProps) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [rectSelection, setRectSelection] = useState<{ x: number; y: number; width: number; height: number } | null>(
    null,
  );
  const [eraseRectSelection, setEraseRectSelection] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [translateRectSelection, setTranslateRectSelection] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [texts, setTexts] = useState<TextElement[]>([]);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editingTextPosition, setEditingTextPosition] = useState<{ x: number; y: number } | null>(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);
  const isEditingTextRef = useRef(false); // Ref for immediate access

  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Debug text editing state
  useEffect(() => {
    console.info('📝 Text editing state changed:', { isEditingText, editingTextPosition, editingTextValue });
  }, [isEditingText, editingTextPosition, editingTextValue]);

  const stageRef = useRef<Konva.Stage>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const initialImageUrlRef = useRef<string>('');

  // Convert Stage to image including all texts
  const convertStageToDataUrl = useCallback(() => {
    if (!stageRef.current) {
      return initialImageUrlRef.current;
    }

    try {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 1 });
      return dataUrl;
    } catch (error) {
      console.error('Failed to convert stage to dataURL:', error);
      return initialImageUrlRef.current;
    }
  }, []);

  // Expose getCurrentEditedImage function to parent
  useEffect(() => {
    if (getCurrentEditedImage) {
      getCurrentEditedImage.current = () => {
        // If there are texts, convert stage to include them
        if (texts.length > 0) {
          return convertStageToDataUrl();
        }
        return initialImageUrlRef.current;
      };
    }
  }, [getCurrentEditedImage, texts, convertStageToDataUrl]);
  const historyRef = useRef<HistoryState[]>([]);
  const historyIndexRef = useRef(-1);

  // Sync refs with state
  useEffect(() => {
    historyRef.current = history;
    historyIndexRef.current = historyIndex;
  }, [history, historyIndex]);

  const updateStageSize = useCallback((img: HTMLImageElement) => {
    if (!containerRef.current) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const scaleToFit = containerWidth / img.width;

    setStageSize({
      width: Math.floor(containerWidth),
      height: Math.floor(img.height * scaleToFit),
    });
  }, []);

  const addToHistory = useCallback((newImageUrl: string, newTexts: TextElement[]) => {
    const currentIndex = historyIndexRef.current;
    const currentHistory = historyRef.current;

    const newHistory = currentHistory.slice(0, currentIndex + 1);
    newHistory.push({ imageUrl: newImageUrl, texts: newTexts });
    const newIndex = newHistory.length - 1;

    console.info('📝 Added to history:', {
      currentIndex,
      newIndex,
      totalStates: newHistory.length,
      canUndo: newIndex > 0,
    });

    // Update refs immediately before state
    historyRef.current = newHistory;
    historyIndexRef.current = newIndex;

    // Then update state
    setHistory(newHistory);
    setHistoryIndex(newIndex);
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      if (state) {
        setHistoryIndex(newIndex);
        setTexts(state.texts);

        const img = new Image();
        if (!state.imageUrl.startsWith('data:')) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => {
          setImage(img);
          initialImageUrlRef.current = state.imageUrl;
          // Don't call onSave during undo - just restore image
          // onSave(state.imageUrl);
        };
        // Use URL directly - don't convert for undo/redo
        img.src = state.imageUrl;
        console.info('↶ Undo: Loading image from history', { url: state.imageUrl.substring(0, 60) });
      }
    }
  }, [historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const state = history[newIndex];
      if (state) {
        setHistoryIndex(newIndex);
        setTexts(state.texts);

        const img = new Image();
        if (!state.imageUrl.startsWith('data:')) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => {
          setImage(img);
          initialImageUrlRef.current = state.imageUrl;
          // Don't call onSave during redo - just restore image
          // onSave(state.imageUrl);
        };
        // Use URL directly - don't convert for undo/redo
        img.src = state.imageUrl;
        console.info('↷ Redo: Loading image from history', { url: state.imageUrl.substring(0, 60) });
      }
    }
  }, [historyIndex, history]);

  // Convert image to data URL for in-memory editing
  const convertImageToDataUrl = useCallback((img: HTMLImageElement): string => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/webp', 1);
  }, []);

  // Complete text editing and add text to canvas
  const handleCompleteTextEditing = useCallback(() => {
    console.info('🎬 Complete text editing called', { editingTextValue });

    // If no text entered, just cancel
    if (!editingTextValue.trim()) {
      console.info('❌ No text entered, canceling');
      setIsEditingText(false);
      isEditingTextRef.current = false;
      setEditingTextPosition(null);
      setEditingTextValue('');
      return;
    }

    if (!editingTextPosition) {
      return;
    }

    const newText: TextElement = {
      id: `text-${Date.now()}`,
      text: editingTextValue.trim(),
      x: editingTextPosition.x,
      y: editingTextPosition.y,
      fontSize: textSettings?.fontSize || 18,
      fontFamily: 'Arial',
      fill: textSettings?.color || '#1a1a1a',
    };
    const newTexts = [...texts, newText];
    console.info('📝 Adding text:', { newText, currentTextsCount: texts.length, newTextsCount: newTexts.length });
    setTexts(newTexts);
    setSelectedTextId(newText.id);

    // Clear editing state
    setIsEditingText(false);
    isEditingTextRef.current = false;
    setEditingTextPosition(null);
    setEditingTextValue('');
    console.info('🔚 Text editing completed, input should be hidden');

    // Wait for next frame to ensure text is rendered, then convert stage to image
    setTimeout(() => {
      if (stageRef.current) {
        const stageDataUrl = convertStageToDataUrl();
        initialImageUrlRef.current = stageDataUrl;
        addToHistory(stageDataUrl, newTexts);
        console.info('✅ Text added and stage converted to image', { textsInHistory: newTexts.length });
      }
    }, 100);
  }, [
    editingTextValue,
    editingTextPosition,
    texts,
    convertStageToDataUrl,
    addToHistory,
    textSettings?.color,
    textSettings?.fontSize,
  ]);

  const handleApplyRectCutout = useCallback(() => {
    console.info('🔪 handleApplyRectCutout called', {
      hasRectSelection: Boolean(rectSelection),
      hasImage: Boolean(image),
      hasStageRef: Boolean(stageRef.current),
      rectSelection,
    });

    if (!rectSelection || !image || !stageRef.current) {
      console.warn('⚠️ Rect cutout aborted - missing requirements');
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      const scaleY = image.height / stageSize.height;

      const startY = Math.min(rectSelection.y, rectSelection.y + rectSelection.height);
      const endY = Math.max(rectSelection.y, rectSelection.y + rectSelection.height);

      const topHeight = startY * scaleY;
      const bottomHeight = image.height - endY * scaleY;

      canvas.width = image.width;
      canvas.height = topHeight + bottomHeight;

      if (topHeight > 0) {
        ctx.drawImage(image, 0, 0, image.width, topHeight, 0, 0, image.width, topHeight);
      }

      if (bottomHeight > 0) {
        ctx.drawImage(image, 0, endY * scaleY, image.width, bottomHeight, 0, topHeight, image.width, bottomHeight);
      }

      const newImageUrl = canvas.toDataURL('image/webp');
      console.info('✅ Rect cutout complete, loading new image');

      // Load the new image and display it
      const img = new Image();
      img.onload = () => {
        setImage(img);
        updateStageSize(img);
        addToHistory(newImageUrl, texts);
        initialImageUrlRef.current = newImageUrl;
        // Don't call onSave immediately - it will reset history
        console.info('✅ Rect cutout image loaded and displayed');
      };
      img.src = newImageUrl;

      setRectSelection(null);
    } catch (error) {
      console.error('Error in rect cutout:', error);
    }
  }, [rectSelection, image, stageSize, texts, addToHistory, updateStageSize]);

  const handleApplyErase = useCallback(() => {
    if (!eraseRectSelection || !image || !stageRef.current) {
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const scaleX = image.width / stageSize.width;
    const scaleY = image.height / stageSize.height;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.drawImage(image, 0, 0);

    const x = Math.round(Math.min(eraseRectSelection.x, eraseRectSelection.x + eraseRectSelection.width) * scaleX);
    const y = Math.round(Math.min(eraseRectSelection.y, eraseRectSelection.y + eraseRectSelection.height) * scaleY);
    const w = Math.round(Math.abs(eraseRectSelection.width * scaleX));
    const h = Math.round(Math.abs(eraseRectSelection.height * scaleY));

    // Sample background color
    const imageData = ctx.getImageData(0, 0, image.width, image.height);
    const border = 10;
    const samples: number[][] = [];

    for (let i = Math.max(0, x - border); i < Math.min(image.width, x + w + border); i += 2) {
      for (let j = Math.max(0, y - border); j < Math.min(image.height, y + h + border); j += 2) {
        if (
          (i < x || i >= x + w || j < y || j >= y + h) &&
          (i < x - 2 || i >= x + w + 2 || j < y - 2 || j >= y + h + 2)
        ) {
          const idx = (j * image.width + i) * 4;
          samples.push([imageData.data[idx]!, imageData.data[idx + 1]!, imageData.data[idx + 2]!]);
        }
      }
    }

    let bgColor = '#ffffff';
    if (samples.length > 0) {
      const medianChannel = (channel: number) => {
        const values = samples.map((s) => s[channel]!).sort((a, b) => a - b);
        return values[Math.floor(values.length / 2)]!;
      };

      const r = medianChannel(0);
      const g = medianChannel(1);
      const b = medianChannel(2);
      bgColor = `rgb(${r}, ${g}, ${b})`;
    }

    ctx.fillStyle = bgColor;
    ctx.fillRect(x, y, w, h);

    const newImageUrl = canvas.toDataURL('image/webp');

    // Load the new image and display it
    const img = new Image();
    img.onload = () => {
      setImage(img);
      updateStageSize(img);
      addToHistory(newImageUrl, texts);
      initialImageUrlRef.current = newImageUrl;
      // Don't call onSave immediately - it will reset history
      console.info('✅ Erase applied and displayed');
    };
    img.src = newImageUrl;

    setEraseRectSelection(null);
  }, [eraseRectSelection, image, stageSize, texts, addToHistory, updateStageSize]);

  const handleApplyTranslate = useCallback(async () => {
    if (!translateRectSelection || !image || !stageRef.current) {
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const scaleX = image.width / stageSize.width;
    const scaleY = image.height / stageSize.height;

    // Extract the selected area
    const x = Math.round(
      Math.min(translateRectSelection.x, translateRectSelection.x + translateRectSelection.width) * scaleX,
    );
    const y = Math.round(
      Math.min(translateRectSelection.y, translateRectSelection.y + translateRectSelection.height) * scaleY,
    );
    const w = Math.round(Math.abs(translateRectSelection.width * scaleX));
    const h = Math.round(Math.abs(translateRectSelection.height * scaleY));

    // Create a canvas for the selected area
    const selectedCanvas = document.createElement('canvas');
    const selectedCtx = selectedCanvas.getContext('2d');
    if (!selectedCtx) {
      return;
    }

    selectedCanvas.width = w;
    selectedCanvas.height = h;

    // Draw the selected area
    selectedCtx.drawImage(image, x, y, w, h, 0, 0, w, h);

    const imageData = selectedCanvas.toDataURL('image/webp');
    const result = await sourcingClient.translateImageFromData(imageData, 'selected_area.png', 'en');

    if (result.success && result.translatedUrl) {
      // Load the translated image
      const translatedImg = new window.Image();
      translatedImg.crossOrigin = 'anonymous';

      translatedImg.onload = () => {
        // Create a new canvas with the full image
        canvas.width = image.width;
        canvas.height = image.height;

        // Draw original image
        ctx.drawImage(image, 0, 0);

        // Draw translated image over the selected area
        ctx.drawImage(translatedImg, 0, 0, translatedImg.width, translatedImg.height, x, y, w, h);

        const newImageUrl = canvas.toDataURL('image/webp');

        // Load the new image and display it
        const img = new Image();
        img.onload = () => {
          setImage(img);
          updateStageSize(img);
          addToHistory(newImageUrl, texts);
          initialImageUrlRef.current = newImageUrl;
          // Don't call onSave immediately - it will reset history
          setTranslateRectSelection(null);
          console.info('✅ Translation applied and displayed');
        };
        img.src = newImageUrl;
      };

      translatedImg.src = result.translatedUrl;
    } else {
      alert(`Translation failed: ${result.message || 'Unknown error'}`);
      setTranslateRectSelection(null);
    }
  }, [translateRectSelection, image, stageSize, texts, addToHistory, updateStageSize]);

  useEffect(() => {
    if (preloadedImage) {
      setImage(preloadedImage);
      setTimeout(() => updateStageSize(preloadedImage), 10);
      setRectSelection(null);
      setEraseRectSelection(null);

      // Only initialize history if switching to a different actual image (not data URL updates)
      if (!imageUrl.startsWith('data:') && initialImageUrlRef.current !== imageUrl) {
        // Convert to data URL for in-memory editing
        const dataUrl = convertImageToDataUrl(preloadedImage);
        console.info('🎬 Image loaded - initializing history', {
          originalUrl: imageUrl.substring(0, 60),
          dataUrlSize: `${Math.round(dataUrl.length / 1024)}KB`,
        });
        initialImageUrlRef.current = dataUrl;
        setHistory([{ imageUrl: dataUrl, texts: [] }]);
        setHistoryIndex(0);
        setTexts([]);
      } else {
        console.info('📝 Skipping history initialization - keeping existing history');
      }
      return;
    }

    if (!imageUrl) {
      return;
    }

    // Convert CloudFront URL to S3 URL for Konva editor (CORS compatibility)
    const convertedUrl = imageUrl.startsWith('data:') ? imageUrl : convertToS3Url(imageUrl);

    const img = new Image();
    if (!imageUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      setImage(img);
      setTimeout(() => updateStageSize(img), 10);
      setRectSelection(null);
      setEraseRectSelection(null);

      // Only initialize history if switching to a different actual image (not data URL updates)
      if (!imageUrl.startsWith('data:') && initialImageUrlRef.current !== imageUrl) {
        // Convert to data URL for in-memory editing
        const dataUrl = convertImageToDataUrl(img);
        console.info('🎬 Image loaded - initializing history', {
          originalUrl: imageUrl.substring(0, 60),
          convertedUrl: convertedUrl.substring(0, 60),
          dataUrlSize: `${Math.round(dataUrl.length / 1024)}KB`,
        });
        initialImageUrlRef.current = dataUrl;
        setHistory([{ imageUrl: dataUrl, texts: [] }]);
        setHistoryIndex(0);
        setTexts([]);
      } else {
        console.info('📝 Skipping history initialization - keeping existing history');
      }
    };

    img.onerror = () => {
      console.error('Failed to load image:', convertedUrl.substring(0, 80));
    };

    img.src = convertedUrl;
  }, [imageUrl, preloadedImage, updateStageSize, convertImageToDataUrl]);

  useEffect(() => {
    if (!image || !containerRef.current) {
      return;
    }

    const updateSize = () => {
      updateStageSize(image);
    };

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [image, updateStageSize]);

  // Update transformer when selected text changes
  useEffect(() => {
    if (!transformerRef.current) {
      return;
    }

    if (selectedTextId) {
      const selectedNode = stageRef.current?.findOne(`#${selectedTextId}`);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer()?.batchDraw();
      }
    } else {
      transformerRef.current.nodes([]);
    }
  }, [selectedTextId]);

  // Keyboard events for deleting selected text and undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedTextId) {
        const newTexts = texts.filter((t) => t.id !== selectedTextId);
        setTexts(newTexts);
        setSelectedTextId(null);

        // Wait for next frame to ensure text is removed, then convert stage to image
        setTimeout(() => {
          if (stageRef.current) {
            const stageDataUrl = convertStageToDataUrl();
            initialImageUrlRef.current = stageDataUrl;
            addToHistory(stageDataUrl, newTexts);
            console.info('✅ Text deleted and stage converted to image');
          }
        }, 100);
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTextId, texts, imageUrl, addToHistory, handleUndo, handleRedo, convertStageToDataUrl]);

  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!stageRef.current || !image) {
      return;
    }

    // Check if clicking on a text element (check both type and if it has a text id)
    const targetType = e.target.getType();
    const targetId = e.target.attrs.id;
    const clickedOnText = targetType === 'Text' || (targetId && texts.some((t) => t.id === targetId));

    if (!clickedOnText) {
      setSelectedTextId(null);
    }

    const pos = stageRef.current.getPointerPosition();
    if (!pos) {
      return;
    }

    const constrainedX = Math.max(0, Math.min(pos.x, stageSize.width));
    const constrainedY = Math.max(0, Math.min(pos.y, stageSize.height));

    console.info('🖱️ Mouse down:', {
      tool,
      clickedOnText,
      targetType,
      targetId,
      constrainedX,
      constrainedY,
      isEditingText,
      isEditingTextRef: isEditingTextRef.current,
    });

    if (tool === 'text' && !clickedOnText) {
      // If already editing, ignore new clicks (use ref for immediate check)
      if (isEditingTextRef.current) {
        console.info('⚠️ Already editing text, ignoring click');
        return;
      }

      console.info('✏️ Starting text editing mode');
      // Start text editing mode at click position
      setIsEditingText(true);
      isEditingTextRef.current = true;
      setEditingTextPosition({ x: constrainedX, y: constrainedY });
      setEditingTextValue('');
      // Focus the input after state update and rendering
      setTimeout(() => {
        console.info('🎯 Focusing text input', textInputRef.current);
        if (textInputRef.current) {
          textInputRef.current.focus();
        }
      }, 50);
    } else if (tool === 'rect-cutout') {
      setIsDrawing(true);
      setRectSelection({ x: 0, y: constrainedY, width: stageSize.width, height: 0 });
    } else if (tool === 'erase') {
      setIsDrawing(true);
      setEraseRectSelection({ x: constrainedX, y: constrainedY, width: 0, height: 0 });
    } else if (tool === 'translate' && !clickedOnText) {
      setIsDrawing(true);
      setTranslateRectSelection({ x: constrainedX, y: constrainedY, width: 0, height: 0 });
    }
  };

  const handleMouseMove = () => {
    if (!stageRef.current || !isDrawing) {
      return;
    }

    const pos = stageRef.current.getPointerPosition();
    if (!pos) {
      return;
    }

    const constrainedX = Math.max(0, Math.min(pos.x, stageSize.width));
    const constrainedY = Math.max(0, Math.min(pos.y, stageSize.height));

    if (tool === 'rect-cutout' && rectSelection) {
      setRectSelection({
        x: 0,
        y: rectSelection.y,
        width: stageSize.width,
        height: constrainedY - rectSelection.y,
      });
    } else if (tool === 'erase' && eraseRectSelection) {
      setEraseRectSelection({
        x: eraseRectSelection.x,
        y: eraseRectSelection.y,
        width: constrainedX - eraseRectSelection.x,
        height: constrainedY - eraseRectSelection.y,
      });
    } else if (tool === 'translate' && translateRectSelection) {
      setTranslateRectSelection({
        x: translateRectSelection.x,
        y: translateRectSelection.y,
        width: constrainedX - translateRectSelection.x,
        height: constrainedY - translateRectSelection.y,
      });
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) {
      return;
    }
    setIsDrawing(false);

    console.info('🖱️ Mouse up detected', {
      tool,
      rectSelection,
      eraseRectSelection,
      translateRectSelection,
    });

    // Auto-apply on mouse up
    if (tool === 'rect-cutout' && rectSelection && Math.abs(rectSelection.height) > 10) {
      console.info('✅ Rect cutout conditions met, scheduling apply');
      setTimeout(() => handleApplyRectCutout(), 100);
    } else if (
      tool === 'erase' &&
      eraseRectSelection &&
      Math.abs(eraseRectSelection.width) > 10 &&
      Math.abs(eraseRectSelection.height) > 10
    ) {
      console.info('✅ Erase conditions met, scheduling apply');
      setTimeout(() => handleApplyErase(), 100);
    } else if (
      tool === 'translate' &&
      translateRectSelection &&
      Math.abs(translateRectSelection.width) > 10 &&
      Math.abs(translateRectSelection.height) > 10
    ) {
      console.info('✅ Translate conditions met, scheduling apply');
      setTimeout(() => handleApplyTranslate(), 100);
    } else {
      console.warn('⚠️ No action triggered on mouse up');
    }
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Debug logging
  useEffect(() => {
    console.info('🔍 History State:', {
      historyIndex,
      historyLength: history.length,
      canUndo,
      canRedo,
      historyItems: history.map((h, i) => ({ index: i, hasImage: Boolean(h.imageUrl), textCount: h.texts.length })),
    });
  }, [historyIndex, history, canUndo, canRedo]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Text input overlay */}
      {isEditingText && editingTextPosition && (
        <input
          ref={textInputRef}
          type="text"
          value={editingTextValue}
          onChange={(e) => setEditingTextValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCompleteTextEditing();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setIsEditingText(false);
              isEditingTextRef.current = false;
              setEditingTextPosition(null);
              setEditingTextValue('');
              console.info('🚫 Text editing cancelled');
            }
          }}
          placeholder=""
          style={{
            position: 'absolute',
            left: `${editingTextPosition.x - 8}px`,
            top: `${editingTextPosition.y - 4}px`,
            fontSize: `${textSettings?.fontSize || 18}px`,
            fontFamily: 'Arial',
            fontWeight: '600',
            color: textSettings?.color || '#1a1a1a',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px dashed rgba(118, 121, 124, 0.6)',
            borderRadius: '2px',
            padding: '2px 4px',
            width: editingTextValue
              ? `${Math.max(editingTextValue.length * ((textSettings?.fontSize || 18) * 0.6), 120)}px`
              : '120px',
            zIndex: 1000,
            outline: 'none',
          }}
        />
      )}
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          cursor:
            tool === 'text'
              ? 'text'
              : tool === 'rect-cutout' || tool === 'erase' || tool === 'translate'
                ? 'crosshair'
                : 'default',
        }}
      >
        <Layer ref={layerRef}>
          {image && <KonvaImage image={image as any} width={stageSize.width} height={stageSize.height} />}

          {eraseRectSelection && eraseRectSelection.width !== 0 && eraseRectSelection.height !== 0 && (
            <Rect
              x={eraseRectSelection.width < 0 ? eraseRectSelection.x + eraseRectSelection.width : eraseRectSelection.x}
              y={
                eraseRectSelection.height < 0 ? eraseRectSelection.y + eraseRectSelection.height : eraseRectSelection.y
              }
              width={Math.abs(eraseRectSelection.width)}
              height={Math.abs(eraseRectSelection.height)}
              stroke="#ffaa00"
              strokeWidth={1}
              dash={[10, 5]}
              fill="rgba(255, 255, 0, 0.3)"
            />
          )}

          {rectSelection && rectSelection.width !== 0 && rectSelection.height !== 0 && (
            <Rect
              x={rectSelection.width < 0 ? rectSelection.x + rectSelection.width : rectSelection.x}
              y={rectSelection.height < 0 ? rectSelection.y + rectSelection.height : rectSelection.y}
              width={Math.abs(rectSelection.width)}
              height={Math.abs(rectSelection.height)}
              stroke="#ff0000"
              strokeWidth={1}
              dash={[10, 5]}
              fill="rgba(255, 0, 0, 0.2)"
            />
          )}

          {translateRectSelection && translateRectSelection.width !== 0 && translateRectSelection.height !== 0 && (
            <Rect
              x={
                translateRectSelection.width < 0
                  ? translateRectSelection.x + translateRectSelection.width
                  : translateRectSelection.x
              }
              y={
                translateRectSelection.height < 0
                  ? translateRectSelection.y + translateRectSelection.height
                  : translateRectSelection.y
              }
              width={Math.abs(translateRectSelection.width)}
              height={Math.abs(translateRectSelection.height)}
              stroke="#00aaff"
              strokeWidth={1}
              dash={[10, 5]}
              fill="rgba(0, 170, 255, 0.3)"
            />
          )}

          {/* Render text elements */}
          {texts.map((textElement) => (
            <KonvaText
              key={textElement.id}
              id={textElement.id}
              text={textElement.text}
              x={textElement.x}
              y={textElement.y}
              fontSize={textElement.fontSize}
              fontFamily={textElement.fontFamily}
              fill={textElement.fill}
              draggable
              onClick={() => setSelectedTextId(textElement.id)}
              onTap={() => setSelectedTextId(textElement.id)}
              onDragEnd={(e) => {
                const updatedTexts = texts.map((t) =>
                  t.id === textElement.id ? { ...t, x: e.target.x(), y: e.target.y() } : t,
                );
                setTexts(updatedTexts);

                // Wait for next frame to ensure text is updated, then convert stage to image
                setTimeout(() => {
                  if (stageRef.current) {
                    const stageDataUrl = convertStageToDataUrl();
                    initialImageUrlRef.current = stageDataUrl;
                    addToHistory(stageDataUrl, updatedTexts);
                    console.info('✅ Text moved and stage converted to image');
                  }
                }, 100);
              }}
              onTransformEnd={(e) => {
                const node = e.target;
                const scaleX = node.scaleX();

                // Reset scale to 1 and apply changes to font size
                node.scaleX(1);
                node.scaleY(1);

                const updatedTexts = texts.map((t) =>
                  t.id === textElement.id
                    ? {
                        ...t,
                        x: node.x(),
                        y: node.y(),
                        rotation: node.rotation(),
                        fontSize: Math.max(5, t.fontSize * scaleX),
                      }
                    : t,
                );
                setTexts(updatedTexts);
                // Use current image from ref (data URL in memory)
                addToHistory(initialImageUrlRef.current || imageUrl, updatedTexts);
              }}
            />
          ))}

          {/* Transformer for text */}
          <Transformer ref={transformerRef} />
        </Layer>
      </Stage>
      <div
        style={{
          padding: '8px 12px',
          borderTop: '1px solid #dee2e6',
          background: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            variant={canUndo ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={handleUndo}
            disabled={!canUndo}
            title={`Undo (Ctrl+Z) - idx:${historyIndex}, len:${history.length}`}
          >
            <Undo size={16} />
          </Button>
          <Button
            variant={canRedo ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={handleRedo}
            disabled={!canRedo}
            title={`Redo (Ctrl+Y) - idx:${historyIndex}, len:${history.length}`}
          >
            <Redo size={16} />
          </Button>
          <span
            style={{
              fontSize: '11px',
              color: '#666',
              fontWeight: 600,
              background: '#e9ecef',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
          >
            {historyIndex + 1}/{history.length}
          </span>
        </div>
        <span
          style={{
            fontSize: '0.85rem',
            color: '#495057',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            flex: 1,
            textAlign: 'right',
          }}
        >
          {(() => {
            // Show "Edited (not saved)" if there are edits in history (more than initial state)
            if (history.length > 1) {
              return 'Edited (not saved)';
            }
            // Otherwise show the original filename
            return imageUrl.split('/').pop();
          })()}
        </span>
        {renderActions && renderActions()}
      </div>
    </div>
  );
};
